/** Input blocks (owner feedback F21): every kind of value a person can enter, each bound to one piece of the component's state. */
import { L, P, defineBlock } from './custom-blocks';
import { WORK, ex } from './custom-blocks-kit';

const base = {
  label: P.tpl('Label', 'Bezeichnung', { def: 'Field' }),
  help: P.tpl('Help text', 'Hilfetext', { opt: true }),
  required: P.bool('Required', 'Pflichtfeld', false),
  disabledWhen: P.expr('Disabled when', 'Deaktiviert, wenn', { expect: 'boolean', opt: true, advanced: true }),
};
const choiceOptions = [
  { key: 'option_1', label: L('First option', 'Erste Möglichkeit'), tone: 'neutral' },
  { key: 'option_2', label: L('Second option', 'Zweite Möglichkeit'), tone: 'neutral' },
  { key: 'option_3', label: L('Third option', 'Dritte Möglichkeit'), tone: 'neutral' },
] as const;
const textState = { key: 'value', type: { kind: 'text' } } as const;

export const inputBlocks = {
  textfield: defineBlock({
    name: L('Text field', 'Textfeld'),
    desc: L('A one-line text input, also for e-mail addresses, phone numbers, web addresses and search.', 'Ein einzeiliges Texteingabefeld, auch für E-Mail-Adressen, Telefonnummern, Webadressen und die Suche.'),
    family: 'input',
    icon: 'type',
    keywords: ['input', 'text', 'email', 'phone', 'url', 'search', 'name', 'eingabe'],
    summary: 'A text input bound to a text state. kind email, phone, url check their format; search draws a search box.',
    props: {
      bind: P.state('Value is kept in', 'Wert wird gespeichert in', ['text']),
      kind: P.pick('Kind', 'Art', ['text', 'email', 'phone', 'url', 'search'] as const, 'text', { labels: { text: L('Text', 'Text'), email: L('E-mail address', 'E-Mail-Adresse'), phone: L('Phone number', 'Telefonnummer'), url: L('Web address', 'Webadresse'), search: L('Search', 'Suche') } }),
      ...base,
      placeholder: P.tpl('Placeholder', 'Platzhalter', { opt: true }),
      maxLength: P.num('Longest text', 'Längster Text', { def: 200, min: 1, max: 2000, int: true, advanced: true }),
    },
    presets: [
      { id: 'email', name: L('E-mail field', 'E-Mail-Feld'), icon: 'mail', props: { kind: 'email', label: L('E-mail', 'E-Mail'), placeholder: L('name@company.com', 'name@firma.de') } },
      { id: 'phone', name: L('Phone field', 'Telefonfeld'), icon: 'phone', props: { kind: 'phone', label: L('Phone', 'Telefon'), placeholder: L('+49 30 1234567', '+49 30 1234567') } },
      { id: 'url', name: L('Web address', 'Webadresse'), icon: 'link', props: { kind: 'url', label: L('Website', 'Webseite'), placeholder: L('https://', 'https://') } },
      { id: 'search', name: L('Search box', 'Suchfeld'), icon: 'search', props: { kind: 'search', label: L('Search', 'Suche'), placeholder: L('Search…', 'Suchen …') } },
    ],
    example: { props: { bind: 'value', label: L('Name', 'Name'), placeholder: L('Your full name', 'Ihr vollständiger Name') }, state: [textState] },
  }),

  choice: defineBlock({
    name: L('Choice', 'Auswahl'),
    desc: L('Pick one option: as a dropdown, radio buttons, a segmented control, chips or cards.', 'Eine Möglichkeit wählen: als Auswahlliste, Optionsfelder, Umschalter, Chips oder Karten.'),
    family: 'input',
    icon: 'bullets',
    keywords: ['select', 'dropdown', 'radio', 'segmented', 'options', 'pick one', 'combobox'],
    summary: 'Pick one of a few options. options = [{key,label,tone}] or source + value + text per row. style: select, radio, segmented, chips, cards. Binds to a text or choice state.',
    scopes: { row: 'source' },
    props: {
      bind: P.state('Value is kept in', 'Wert wird gespeichert in', ['text', 'choice']),
      style: P.pick('Looks like', 'Darstellung', ['select', 'radio', 'segmented', 'chips', 'cards'] as const, 'select', { labels: { select: L('Dropdown', 'Auswahlliste'), radio: L('Radio buttons', 'Optionsfelder'), segmented: L('Segmented control', 'Umschalter'), chips: L('Chips', 'Chips'), cards: L('Cards', 'Karten') } }),
      ...base,
      options: P.options('Options', 'Möglichkeiten', { max: 20, def: [...choiceOptions] as never }),
      source: P.source('Or take options from data', 'Oder Möglichkeiten aus Daten', ['records', 'static'], { advanced: true, opt: true }),
      value: P.expr('Value of an option', 'Wert einer Möglichkeit', { expect: 'text', scope: 'row', opt: true, advanced: true }),
      text: P.expr('Name of an option', 'Name einer Möglichkeit', { expect: 'text', scope: 'row', opt: true, advanced: true }),
    },
    presets: [
      { id: 'radio', name: L('Radio buttons', 'Optionsfelder'), icon: 'radio', props: { style: 'radio' } },
      { id: 'segmented', name: L('Segments', 'Segmente'), icon: 'columns-2', props: { style: 'segmented' } },
      { id: 'chips', name: L('Chip choice', 'Chip-Auswahl'), icon: 'chips', props: { style: 'chips' } },
      { id: 'cards', name: L('Choice cards', 'Auswahlkarten'), icon: 'box', props: { style: 'cards' } },
    ],
    example: { props: { bind: 'value', label: L('Priority', 'Priorität'), options: [{ key: 'low', label: L('Low', 'Niedrig') }, { key: 'normal', label: L('Normal', 'Normal') }, { key: 'high', label: L('High', 'Hoch') }] }, state: [textState] },
  }),

  checkboxes: defineBlock({
    name: L('Checkbox group', 'Kontrollkästchen'),
    desc: L('Pick any number of options from a list.', 'Beliebig viele Möglichkeiten aus einer Liste wählen.'),
    family: 'input',
    icon: 'check',
    keywords: ['multi select', 'multiple', 'checklist', 'tick', 'several'],
    summary: 'Pick several options; the picked keys are kept in a list state.',
    props: {
      bind: P.state('Values are kept in', 'Werte werden gespeichert in', ['list']),
      style: P.pick('Looks like', 'Darstellung', ['list', 'chips'] as const, 'list', { seg: true, labels: { list: L('List', 'Liste'), chips: L('Chips', 'Chips') } }),
      ...base,
      options: P.options('Options', 'Möglichkeiten', { max: 20, def: [...choiceOptions] as never }),
      columns: P.pick('Columns', 'Spalten', ['1', '2'] as const, '1', { seg: true, advanced: true }),
    },
    example: { props: { bind: 'picked', label: L('Days in the office', 'Tage im Büro'), options: [{ key: 'mon', label: L('Monday', 'Montag') }, { key: 'tue', label: L('Tuesday', 'Dienstag') }, { key: 'wed', label: L('Wednesday', 'Mittwoch') }, { key: 'thu', label: L('Thursday', 'Donnerstag') }] }, state: [{ key: 'picked', type: { kind: 'list' } }] },
  }),

  duration: defineBlock({
    name: L('Duration', 'Dauer'),
    desc: L('Hours and minutes: type 1:30, 90m or 1.5h.', 'Stunden und Minuten: 1:30, 90m oder 1,5h eintippen.'),
    family: 'input',
    icon: 'stopwatch',
    keywords: ['hours', 'minutes', 'time spent', 'timesheet', 'length'],
    summary: 'A duration input; the state holds the number of minutes.',
    props: {
      bind: P.state('Minutes are kept in', 'Minuten werden gespeichert in', ['number']),
      ...base,
      step: P.num('Step of the arrows', 'Schritt der Pfeile', { def: 15, min: 1, max: 120, int: true, unit: L('min', 'Min.'), advanced: true }),
      max: P.num('Longest', 'Längste', { def: 1440, min: 1, max: 100000, int: true, unit: L('min', 'Min.'), advanced: true }),
    },
    example: { props: { bind: 'minutes', label: L('Time spent', 'Aufgewendete Zeit'), help: L('Try 1:30 or 90m', 'Probieren Sie 1:30 oder 90m') }, state: [{ key: 'minutes', type: { kind: 'number' } }] },
  }),

  currency: defineBlock({
    name: L('Amount', 'Betrag'),
    desc: L('A money amount with the currency, in the reader’s number format.', 'Ein Geldbetrag mit Währung, im Zahlenformat der Leserin oder des Lesers.'),
    family: 'input',
    icon: 'currency',
    keywords: ['money', 'price', 'euro', 'budget', 'cost', 'price field'],
    summary: 'A money input; the state holds the plain number.',
    props: {
      bind: P.state('Amount is kept in', 'Betrag wird gespeichert in', ['number']),
      currency: P.str('Currency (ISO code)', 'Währung (ISO-Code)', { def: 'EUR', max: 3, pattern: /^[A-Z]{3}$/ }),
      ...base,
      min: P.num('Lowest', 'Niedrigster Wert', { def: 0, advanced: true }),
    },
    example: { props: { bind: 'amount', label: L('Budget', 'Budget'), currency: 'EUR' }, state: [{ key: 'amount', type: { kind: 'number' } }] },
  }),

  daterange: defineBlock({
    name: L('Date range', 'Zeitraum'),
    desc: L('Two dates, from and to, in one calendar.', 'Zwei Daten, von und bis, in einem Kalender.'),
    family: 'input',
    icon: 'calendar',
    keywords: ['from to', 'period', 'vacation', 'start end', 'dates'],
    summary: 'A from/to picker; the two dates are kept in two date states.',
    props: {
      bind: P.state('From is kept in', 'Von wird gespeichert in', ['date']),
      bindTo: P.state('To is kept in', 'Bis wird gespeichert in', ['date']),
      ...base,
      min: P.expr('Earliest day', 'Frühester Tag', { expect: 'date', opt: true, advanced: true }),
      max: P.expr('Latest day', 'Spätester Tag', { expect: 'date', opt: true, advanced: true }),
    },
    example: { props: { bind: 'from', bindTo: 'to', label: L('Dates', 'Zeitraum') }, state: [{ key: 'from', type: { kind: 'date' } }, { key: 'to', type: { kind: 'date' } }] },
  }),

  slider: defineBlock({
    name: L('Slider', 'Schieberegler'),
    desc: L('Drag to pick a number in a range.', 'Ziehen, um eine Zahl in einem Bereich zu wählen.'),
    family: 'input',
    icon: 'sliders',
    keywords: ['range', 'drag', 'percent', 'level'],
    summary: 'A slider from min to max in steps; the state holds the number.',
    props: {
      bind: P.state('Value is kept in', 'Wert wird gespeichert in', ['number']),
      ...base,
      min: P.num('Lowest', 'Niedrigster Wert', { def: 0 }),
      max: P.num('Highest', 'Höchster Wert', { def: 100 }),
      step: P.num('Step', 'Schritt', { def: 5, min: 0.01 }),
      unit: P.str('Unit', 'Einheit', { opt: true, max: 12 }),
    },
    example: { props: { bind: 'level', label: L('Confidence', 'Zuversicht'), min: 0, max: 100, step: 5, unit: '%' }, state: [{ key: 'level', type: { kind: 'number' }, initial: '50' }] },
  }),

  rating: defineBlock({
    name: L('Rating', 'Bewertung'),
    desc: L('Stars (or another symbol) to rate, or to show a rating.', 'Sterne (oder ein anderes Symbol) zum Bewerten oder um eine Bewertung zu zeigen.'),
    family: 'input',
    icon: 'star',
    keywords: ['stars', 'score', 'review', 'feedback', 'nps'],
    summary: 'Click to rate 1 to max; without bind, value shows a read-only rating.',
    props: {
      bind: P.state('Rating is kept in', 'Bewertung wird gespeichert in', ['number'], { opt: true }),
      value: P.expr('Or show this value', 'Oder diesen Wert zeigen', { expect: 'number', opt: true }),
      label: P.tpl('Label', 'Bezeichnung', { opt: true }),
      max: P.num('Number of stars', 'Anzahl der Sterne', { def: 5, min: 3, max: 10, int: true }),
      icon: P.icon({ en: 'Symbol', de: 'Symbol' }),
      required: P.bool('Required', 'Pflichtfeld', false),
    },
    example: { props: { bind: 'stars', label: L('How was it?', 'Wie war es?'), max: 5 }, state: [{ key: 'stars', type: { kind: 'number' }, initial: '4' }] },
  }),

  color: defineBlock({
    name: L('Colour', 'Farbe'),
    desc: L('Pick a colour from swatches or with the picker.', 'Eine Farbe aus Farbfeldern oder mit dem Farbwähler wählen.'),
    family: 'input',
    icon: 'palette',
    keywords: ['picker', 'swatch', 'hex', 'brand colour'],
    summary: 'A colour picker; the state holds the colour as text (#rrggbb).',
    props: { bind: P.state('Colour is kept in', 'Farbe wird gespeichert in', ['text']), ...base },
    example: { props: { bind: 'tint', label: L('Highlight colour', 'Hervorhebungsfarbe') }, state: [{ key: 'tint', type: { kind: 'text' }, initial: '"#444ce7"' }] },
  }),

  upload: defineBlock({
    name: L('Image upload', 'Bild-Upload'),
    desc: L('Drop or choose pictures; they are stored in the workspace and kept as a list.', 'Bilder ablegen oder auswählen; sie werden im Arbeitsbereich gespeichert und als Liste gehalten.'),
    family: 'input',
    icon: 'upload',
    keywords: ['file', 'photo', 'attachment', 'drop', 'picture'],
    summary: 'Uploads images (PNG, JPEG, WebP, GIF, SVG) as workspace assets; the state holds their asset ids. Other files belong on a record file field.',
    props: {
      bind: P.state('Pictures are kept in', 'Bilder werden gespeichert in', ['list']),
      ...base,
      max: P.num('Most pictures', 'Höchstens Bilder', { def: 4, min: 1, max: 20, int: true }),
    },
    example: { props: { bind: 'photos', label: L('Photos', 'Fotos'), max: 3 }, state: [{ key: 'photos', type: { kind: 'list' } }] },
  }),

  signature: defineBlock({
    name: L('Signature pad', 'Unterschriftsfeld'),
    desc: L('Draw a signature with mouse, pen or finger.', 'Eine Unterschrift mit Maus, Stift oder Finger zeichnen.'),
    family: 'input',
    icon: 'signature',
    keywords: ['sign', 'draw', 'autograph', 'consent'],
    summary: 'A drawing pad; applying it stores the signature as a workspace image and keeps its asset id in a text state.',
    props: { bind: P.state('Signature is kept in', 'Unterschrift wird gespeichert in', ['text']), ...base },
    example: { props: { bind: 'signature', label: L('Your signature', 'Ihre Unterschrift') }, state: [{ key: 'signature', type: { kind: 'text' } }] },
  }),

  tagsinput: defineBlock({
    name: L('Tags input', 'Tag-Eingabe'),
    desc: L('Type words and press Enter to add them as tags, with optional suggestions.', 'Wörter tippen und mit Enter als Tags hinzufügen, mit optionalen Vorschlägen.'),
    family: 'input',
    icon: 'hash',
    keywords: ['tokens', 'keywords', 'labels', 'chips input', 'multi value'],
    summary: 'Free tags kept in a list state; suggestions = quick-add options.',
    props: {
      bind: P.state('Tags are kept in', 'Tags werden gespeichert in', ['list']),
      ...base,
      suggestions: P.items('Suggestions', 'Vorschläge', { text: P.str('Tag', 'Tag', { def: '', max: 40 }) }, { titleKey: 'text', max: 12, def: [], addLabel: L('Add a suggestion', 'Vorschlag hinzufügen') }),
      max: P.num('Most tags', 'Höchstens Tags', { def: 12, min: 1, max: 50, int: true, advanced: true }),
    },
    example: { props: { bind: 'tags', label: L('Skills', 'Kenntnisse'), suggestions: [{ text: 'Design' }, { text: 'Research' }, { text: 'Writing' }] }, state: [{ key: 'tags', type: { kind: 'list' } }] },
  }),

  address: defineBlock({
    name: L('Address', 'Adresse'),
    desc: L('Street, postcode, city and country in one block.', 'Straße, Postleitzahl, Ort und Land in einem Block.'),
    family: 'input',
    icon: 'map',
    keywords: ['postal', 'location', 'shipping', 'billing', 'anschrift'],
    summary: 'Four address fields kept together as multi-line text (street, postcode and city, country).',
    props: { bind: P.state('Address is kept in', 'Adresse wird gespeichert in', ['text']), ...base },
    example: { props: { bind: 'address', label: L('Delivery address', 'Lieferadresse') }, state: [{ key: 'address', type: { kind: 'text', long: true } }] },
  }),

  time: defineBlock({
    name: L('Time of day', 'Uhrzeit'),
    desc: L('Hours and minutes of a day, in the 24 or 12 hour clock.', 'Stunden und Minuten eines Tages, im 24- oder 12-Stunden-Format.'),
    family: 'input',
    icon: 'clock',
    keywords: ['hour', 'clock', 'start time', 'appointment'],
    summary: 'A time input; the state holds "HH:mm".',
    props: {
      bind: P.state('Time is kept in', 'Uhrzeit wird gespeichert in', ['text']),
      ...base,
      step: P.num('Step', 'Schritt', { def: 15, min: 1, max: 60, int: true, unit: L('min', 'Min.'), advanced: true }),
    },
    example: { props: { bind: 'at', label: L('Starts at', 'Beginnt um') }, state: [{ key: 'at', type: { kind: 'text' }, initial: '"09:00"' }] },
  }),

  numstep: defineBlock({
    name: L('Number stepper', 'Zahlenfeld mit Pfeilen'),
    desc: L('A number with minus and plus buttons.', 'Eine Zahl mit Minus- und Plus-Schaltflächen.'),
    family: 'input',
    icon: 'plus',
    keywords: ['quantity', 'counter', 'increment', 'spinner', 'count'],
    summary: 'A number with − and + buttons within min and max.',
    props: {
      bind: P.state('Number is kept in', 'Zahl wird gespeichert in', ['number']),
      ...base,
      min: P.num('Lowest', 'Niedrigster Wert', { def: 0 }),
      max: P.num('Highest', 'Höchster Wert', { def: 99 }),
      step: P.num('Step', 'Schritt', { def: 1, min: 0.01 }),
    },
    example: { props: { bind: 'qty', label: L('Quantity', 'Menge'), min: 1, max: 20 }, state: [{ key: 'qty', type: { kind: 'number' }, initial: '1' }] },
  }),

  fieldset: defineBlock({
    name: L('Form section', 'Formularabschnitt'),
    desc: L('A titled group of inputs in one or two columns, for long forms.', 'Eine Gruppe von Eingaben mit Titel in ein oder zwei Spalten, für lange Formulare.'),
    family: 'input',
    icon: 'fieldset',
    keywords: ['group', 'legend', 'section', 'multi section form', 'fieldset'],
    summary: 'Groups inputs under a legend. Use several in a form (or a wizard) for a multi-section form.',
    children: 'many',
    props: {
      legend: P.tpl('Title', 'Titel', { def: 'Section' }),
      description: P.tpl('Description', 'Beschreibung', { opt: true }),
      columns: P.pick('Columns', 'Spalten', ['1', '2'] as const, '1', { seg: true }),
    },
    example: {
      props: { legend: L('Contact', 'Kontakt'), description: L('How we can reach you', 'Wie wir Sie erreichen'), columns: '2' },
      children: [
        { type: 'textfield', props: { bind: 'name', label: L('Name', 'Name'), required: true } },
        { type: 'textfield', props: { bind: 'mail', kind: 'email', label: L('E-mail', 'E-Mail') } },
      ],
      state: [{ key: 'name', type: { kind: 'text' } }, { key: 'mail', type: { kind: 'text' } }],
    },
  }),
};

void WORK;
void ex;
